import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Minus, Plus } from 'lucide-react'
import { BottomSheet } from '@/shared/components/BottomSheet'

interface ServingEditorProps {
  currentMultiplier: number
  recipeServingSize: number
  onSave: (multiplier: number) => void
  onClose: () => void
}

export function ServingEditor({ currentMultiplier, recipeServingSize, onSave, onClose }: ServingEditorProps) {
  const { t } = useTranslation('meal-plan')
  const [multiplier, setMultiplier] = useState(currentMultiplier)

  const portions = Math.round(recipeServingSize * multiplier)

  const handleIncrement = () => {
    setMultiplier((prev) => Math.min(prev + 0.5, 20.0))
  }

  const handleDecrement = () => {
    setMultiplier((prev) => Math.max(prev - 0.5, 0.5))
  }

  const handleSave = () => {
    onSave(multiplier)
    onClose()
  }

  return (
    <BottomSheet open onClose={onClose} title={t('servingEditor.title')}>
      <div className="flex flex-col items-center gap-6 py-4">
        <div className="flex items-center gap-6">
          <button
            type="button"
            onClick={handleDecrement}
            disabled={multiplier <= 0.5}
            aria-label={t('servingEditor.decrement')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-line text-ink hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Minus className="h-5 w-5" aria-hidden="true" />
          </button>

          <p className="text-4xl font-bold text-ink" role="status" aria-live="polite">
            {t('servingEditor.portionsLabel', { count: portions })}
          </p>

          <button
            type="button"
            onClick={handleIncrement}
            disabled={multiplier >= 20.0}
            aria-label={t('servingEditor.increment')}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full border border-line text-ink hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <button
          type="button"
          onClick={handleSave}
          className="w-full min-h-[44px] rounded-lg bg-accent text-white font-semibold hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          {t('servingEditor.save')}
        </button>
      </div>
    </BottomSheet>
  )
}
